import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Eye } from 'react-bootstrap-icons'
import { CampaignContext } from '../../../../../contexts/CampaignContext'
import { currencyFormat } from '../../../../../utils/Helper'
import { toastError, toastSuccess } from '../../../../FormComponents/Toasts'
import BudgetHiddenInfluencersList from './BudgetHiddenInfluencersList'
import BudgetDataTable from './BudgetDataTable'
import {
  CLIENT_ALWAYS_HIDDEN_COLUMNS,
  forcedHiddenColumnsFromPlatforms,
  REELS_LIKE_TABLE_KEYS,
  TABLES,
  TABS,
  tabVisibleForCampaignPlatforms,
  tabVisibleForOverviewColumnVisibility,
} from './BudgetDetailsPanelConfig'
import { fixed2 } from './BudgetDetailsPanelNumberUtils'
import {
  budgetTableRowsForHiddenInfluencers,
  buyoutFooterTotals,
  customFieldsTableConfigFromCampaign,
  hiddenInfluencerSlotsForScenario,
  overallKostenFooterTotals,
} from './BudgetDetailsPanelRowBuilders'
import { deleteInfluencerRowSlot, duplicateInfluencerRow } from './BudgetDetailsPanelPersistence'
import useBudgetRows from './useBudgetRows'
import useBudgetColumnVisibility from './useBudgetColumnVisibility'
import useBudgetCellHandlers from './useBudgetCellHandlers'
import { useReadOnly } from '../../../../../contexts/ReadOnlyContext'

export default observer(function BudgetDetailsPanel({
  campaignScenario,
  readOnly = false,
  campaign: campaignProp,
  influencers: influencersProp,
  authentications: authenticationsProp,
}) {
  const campaignContext = useContext(CampaignContext)
  const archived = useReadOnly()
  const noEdit = readOnly || archived
  const campaign = campaignProp ?? campaignContext?.campaign
  const influencers = influencersProp ?? campaignContext?.influencers
  const authentications = authenticationsProp ?? campaignContext?.authentications
  const forcedHiddenColumns = readOnly
    ? {
        ...forcedHiddenColumnsFromPlatforms(campaign?.platforms),
        ...Object.fromEntries([...CLIENT_ALWAYS_HIDDEN_COLUMNS].map(key => [key, true])),
      }
    : forcedHiddenColumnsFromPlatforms(campaign?.platforms)
  const customFieldsTableConfig = useMemo(() => customFieldsTableConfigFromCampaign(campaign), [campaign?.custom_fields])
  const platformsVisibilityKey = [...(campaign?.platforms || [])].sort().join(',')
  const customFieldsKey = useMemo(
    () =>
      JSON.stringify(
        (campaign?.custom_fields || []).map(f => [f?.id, f?.label, f?.field_type, f?.advertiser_fee_enabled, f?.ksa_enabled])
      ),
    [campaign?.custom_fields]
  )
  const hasCustomFields = customFieldsTableConfig.columns.length > 1

  const {
    isColumnEditMode,
    visibleColumnsByTab,
    draftColumnsByTab,
    toggleColumn,
    toggleColumnEditMode,
  } = useBudgetColumnVisibility({
    campaign,
    campaignScenario,
    customFieldsTableConfig,
    platformsVisibilityKey,
    customFieldsKey,
    forcedHiddenColumns,
  })

  const visibleTabs = useMemo(
    () => TABS.filter(tab => tab.visible
      && tabVisibleForCampaignPlatforms(tab, campaign?.platforms)
      && (tab.key !== 'custom' || hasCustomFields)
      && tabVisibleForOverviewColumnVisibility(tab, visibleColumnsByTab.overview)),
    [platformsVisibilityKey, hasCustomFields, campaign?.platforms, visibleColumnsByTab.overview]
  )

  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [showHiddenInfluencers, setShowHiddenInfluencers] = useState(false)

  useEffect(() => {
    if (visibleTabs.length === 0) return
    if (activeTabIndex >= visibleTabs.length) setActiveTabIndex(0)
  }, [visibleTabs.length, activeTabIndex])

  const activeTab = visibleTabs[activeTabIndex] || visibleTabs[0]
  const activeConfig = activeTab?.tableKey === 'custom' ? customFieldsTableConfig : TABLES[activeTab?.tableKey]
  const visibleColumns = activeTab ? (visibleColumnsByTab[activeTab.key] || {}) : {}
  const draftColumns = activeTab ? (draftColumnsByTab?.[activeTab.key] || visibleColumns) : visibleColumns
  const handleToggleColumn = useCallback(
    key => activeTab && toggleColumn(activeTab.key, key),
    [activeTab, toggleColumn]
  )

  const {
    overviewRows,
    setOverviewRows,
    reelsLikeRowsByKey,
    setReelsLikeRowsByKey,
    overallKostenRows,
    setOverallKostenRows,
    buyoutRows,
    setBuyoutRows,
    customFieldBudgetRows,
    setCustomFieldBudgetRows,
    refs,
    pendingOverviewScopeEdits,
    pendingWelcheBuyoutsEdits,
    syncKeys,
  } = useBudgetRows({
    campaignScenario,
    influencers,
    campaign,
    authentications,
    customFieldsKey,
  })

  const {
    handleOverviewCellChange,
    handleReelsLikeCellChange,
    handleOverallKostenCellChange,
    handleBuyoutCellChange,
    handleCustomFieldBudgetCellChange,
    isOverviewScopeSyncing,
    isChannelAuthSyncing,
  } = useBudgetCellHandlers({
    campaign,
    campaignScenario,
    influencers,
    authentications,
    setOverviewRows,
    setReelsLikeRowsByKey,
    setOverallKostenRows,
    setBuyoutRows,
    setCustomFieldBudgetRows,
    refs,
    pendingOverviewScopeEdits,
    pendingWelcheBuyoutsEdits,
  })

  const tabButtons = useMemo(
    () =>
      visibleTabs.map((tab, index) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => setActiveTabIndex(index)}
          className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors whitespace-nowrap ${
            activeTabIndex === index
              ? 'text-rb-green border-rb-green bg-white dark:bg-rb-dark-mode-background'
              : 'text-black border-gray-300 bg-white dark:bg-rb-dark-mode-background dark:text-white'
          }`}
        >
          {tab.label}
        </button>
      )),
    [activeTabIndex, visibleTabs]
  )

  const activeTableKey = activeTab?.tableKey
  const isReelsLikeTable = REELS_LIKE_TABLE_KEYS.includes(activeTableKey)
  const isOverallKostenTable = activeTableKey === 'overall-kosten'
  const isBuyoutsTable = activeTableKey === 'buyouts'
  const isCustomFieldsBudgetTable = activeTableKey === 'custom'
  const dataTableRows =
    activeTableKey === 'overview'
      ? overviewRows ?? []
      : isReelsLikeTable
        ? reelsLikeRowsByKey[activeTableKey] ?? []
        : isOverallKostenTable
          ? overallKostenRows ?? []
          : isBuyoutsTable
            ? buyoutRows ?? []
            : isCustomFieldsBudgetTable
              ? customFieldBudgetRows ?? []
              : []
  const dataTableInputChange = noEdit
    ? undefined
    : activeTableKey === 'overview'
      ? handleOverviewCellChange
      : isReelsLikeTable
        ? (rowIndex, colKey, value) => handleReelsLikeCellChange(activeTableKey, rowIndex, colKey, value)
        : isOverallKostenTable
          ? handleOverallKostenCellChange
          : isBuyoutsTable
            ? handleBuyoutCellChange
            : isCustomFieldsBudgetTable
              ? handleCustomFieldBudgetCellChange
              : undefined

  const dataTableKind =
    activeTableKey === 'overview'
      ? 'overview'
      : isOverallKostenTable
        ? 'overallKosten'
        : isBuyoutsTable
          ? 'buyouts'
          : isCustomFieldsBudgetTable
            ? 'custom'
            : 'reelsLike'

  const hiddenBudgetSlots = useMemo(
    () => hiddenInfluencerSlotsForScenario(campaignScenario, influencers, campaign, authentications),
    [
      influencers,
      campaignScenario,
      campaign,
      authentications,
      syncKeys.scenarioActionGroupsSyncKey,
      syncKeys.authenticationsStatusSyncKey,
    ]
  )

  const hiddenBudgetTableRows = useMemo(
    () =>
      budgetTableRowsForHiddenInfluencers({
        activeTabTableKey: activeTableKey,
        campaignScenario,
        campaign,
        hiddenSlotsList: hiddenBudgetSlots,
        currencySymbol: campaign?.currency_symbol,
      }),
    [
      activeTableKey,
      campaignScenario,
      campaign,
      hiddenBudgetSlots,
      syncKeys.scenarioActionsSyncKey,
      syncKeys.scenarioActionGroupsSyncKey,
      syncKeys.authenticationsStatusSyncKey,
      syncKeys.scenarioCustomFieldValuesSyncKey,
      customFieldsKey,
    ]
  )

  const showInfluencerBudgetVisibilityToggle =
    !noEdit && isColumnEditMode && !!draftColumns.influencer && !forcedHiddenColumns.influencer

  const handleDuplicateInfluencerRow = useCallback(
    async influencerId => {
      if (!campaign?.id || !campaignScenario?.id) return
      try {
        await duplicateInfluencerRow({
          campaignId: campaign.id,
          scenarioId: campaignScenario.id,
          campaignScenario,
          influencerId,
        })
        toastSuccess('Duplicate row added below the original')
      } catch (err) {
        const msg = err?.data?.error ?? err?.message ?? 'Could not duplicate row'
        toastError(typeof msg === 'string' ? msg : 'Could not duplicate row')
      }
    },
    [campaign?.id, campaignScenario]
  )

  const handleDeleteInfluencerRow = useCallback(
    async (influencerId, slotNumber) => {
      if (!campaign?.id || !campaignScenario?.id) return
      try {
        await deleteInfluencerRowSlot({
          campaignId: campaign.id,
          scenarioId: campaignScenario.id,
          campaignScenario,
          influencerId,
          slotNumber,
        })
        toastSuccess('Row deleted')
      } catch (err) {
        const msg = err?.data?.error ?? err?.message ?? 'Could not delete row'
        toastError(typeof msg === 'string' ? msg : 'Could not delete row')
      }
    },
    [campaign?.id, campaignScenario]
  )

  const dataTableConfig = useMemo(() => {
    if (!activeConfig) return activeConfig
    const sym = campaign?.currency_symbol || '€'
    if (activeTableKey === 'overall-kosten' && campaignScenario) {
      const t = overallKostenFooterTotals(campaignScenario, influencers, campaign, authentications)
      return {
        ...activeConfig,
        footer: {
          influencer: 'Total:',
          ksaGesamt: currencyFormat(fixed2(t.sumKsa), sym),
          kostenAdvertiser: currencyFormat(fixed2(t.sumAdvertiser), sym),
          payout: currencyFormat(fixed2(t.sumPayout), sym),
          kommentar: '',
          margeEur: currencyFormat(fixed2(t.sumMargeEur), sym),
          margeBuyouts: '',
          margeMedia: '',
        },
      }
    }
    if (activeTableKey === 'buyouts' && campaignScenario) {
      const t = buyoutFooterTotals(campaignScenario, influencers, campaign, authentications)
      return {
        ...activeConfig,
        footer: {
          influencer: 'Total:',
          anzahlBuyout: String(t.sumAnzahl),
          buyoutUnitPrice: '',
          gesamtBuyouts: currencyFormat(fixed2(t.sumInfluencerBuyout), sym),
          buyoutAdvertiser: currencyFormat(fixed2(t.sumAdvertiserBuyout), sym),
          welcheBuyouts: '',
        },
      }
    }
    return activeConfig
  }, [
    activeTableKey,
    activeConfig,
    campaignScenario,
    influencers,
    campaign,
    authentications,
    syncKeys.scenarioActionsSyncKey,
    syncKeys.scenarioActionGroupsSyncKey,
    syncKeys.authenticationsStatusSyncKey,
    syncKeys.scenarioCustomFieldValuesSyncKey,
    customFieldsKey,
  ])

  return (
    <div className="relative">
      {!noEdit && (
        <div className="flex items-center justify-end mb-3">
          <button
            type="button"
            onClick={toggleColumnEditMode}
            className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 flex items-center gap-2 bg-white"
          >
            <Eye />
            {isColumnEditMode ? 'Confirm' : 'Visibility'}
          </button>
        </div>
      )}

      <div className="mb-4 flex flex-wrap justify-center gap-2">
        {tabButtons}
      </div>

      {!noEdit && activeTableKey === 'overview' && (isOverviewScopeSyncing || isChannelAuthSyncing) && (
        <div className="pointer-events-none absolute left-1/2 top-4 z-10 flex -translate-x-1/2 items-center justify-center gap-2 rounded-xl border border-rb-green/30 bg-white px-3 py-2 text-xs font-semibold text-rb-green shadow-sm dark:bg-rb-dark-mode-background">
          <span className="h-2 w-2 animate-pulse rounded-full bg-rb-green" />
          {isChannelAuthSyncing
            ? 'Saving channel authentication in the background. You can keep editing.'
            : 'Updating actions in the background. You can keep editing.'}
        </div>
      )}

      {dataTableConfig && (
        <BudgetDataTable
          config={dataTableConfig}
          visibleColumns={visibleColumns}
          draftColumns={draftColumns}
          editingColumns={noEdit ? false : isColumnEditMode}
          onToggleColumn={noEdit ? undefined : handleToggleColumn}
          rows={dataTableRows}
          onInputCellChange={dataTableInputChange}
          tableKind={dataTableKind}
          forcedHiddenColumns={forcedHiddenColumns}
          campaignScenario={campaignScenario}
          showInfluencerVisibilityToggle={showInfluencerBudgetVisibilityToggle}
          onDuplicateRow={noEdit ? undefined : handleDuplicateInfluencerRow}
          onDeleteRow={noEdit ? undefined : handleDeleteInfluencerRow}
          forceRowsReadOnly={readOnly}
        />
      )}

      {!readOnly && hiddenBudgetSlots.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border-primary dark:border-rb-dark-mode-lighter">
          <button
            type="button"
            className="text-sm font-semibold text-rb-green dark:text-rb-green"
            onClick={() => setShowHiddenInfluencers(!showHiddenInfluencers)}
          >
            {showHiddenInfluencers ? 'Hide list' : `Show hidden list (${hiddenBudgetSlots.length})`}
          </button>
          {showHiddenInfluencers && (
            <div className="mt-3">
              <BudgetHiddenInfluencersList
                tableConfig={dataTableConfig}
                forcedHiddenColumns={forcedHiddenColumns}
                editingColumns={noEdit ? false : isColumnEditMode}
                draftColumns={draftColumns}
                visibleColumns={visibleColumns}
                onToggleColumn={handleToggleColumn}
                rows={hiddenBudgetTableRows}
                tableKind={dataTableKind}
                campaignScenario={campaignScenario}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
})
